import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Calendar, UserPlus, Video, Copy, Check, X, Clock, Play, MapPin, Mail, Download, Share2, Eye, EyeOff } from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import { meetingService, Meeting } from '../../../services/meeting.service.ts';
import { authService } from '../../../services/auth.service.ts';
import { getMeetingDurationLimitSeconds, getUserPlan, resolveRoomDurationLimitSeconds } from '../../../utils/plan.ts';
import { createRoomTimePolicy, writeRoomTimePolicy } from '../../meeting/utils/roomTimePolicy.ts';
import { createInstantOnlineMeeting, validateMeetingLinkAccess } from '../../meeting/utils/meetingLinkExpiry.ts';
import { filterUpcomingMeetings } from '../../meeting/utils/meetingListFilters.ts';

const QuickActions: React.FC = () => {
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);
  const [showJoinModal, setShowJoinModal] = useState(false);
  const [meetingIdInput, setMeetingIdInput] = useState('');
  const [meetings, setMeetings] = useState<Meeting[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'all' | 'online' | 'inperson'>('all');
  const [showInvitationModal, setShowInvitationModal] = useState(false);
  const [selectedMeeting, setSelectedMeeting] = useState<Meeting | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [showPersonalMeetingId, setShowPersonalMeetingId] = useState(false);
  const [joinError, setJoinError] = useState('');

  const user = authService.getUser();
  const generatePersonalMeetingId = (userId: string): string => {
    const hash = userId.replace(/-/g, '').substring(0, 10);
    const numericId = parseInt(hash, 16) % 10000000000;
    const formatted = numericId.toString().padStart(10, '0');
    return `${formatted.slice(0, 3)} ${formatted.slice(3, 6)} ${formatted.slice(6, 10)}`;
  };

  const maskPersonalMeetingId = (formattedId: string): string => {
    const digits = formattedId.replace(/\s/g, '');
    if (digits.length <= 3) return formattedId;
    const masked = '*'.repeat(digits.length - 3) + digits.slice(-3);
    return `${masked.slice(0, 3)} ${masked.slice(3, 6)} ${masked.slice(6, 10)}`.trim();
  };

  const personalMeetingId = user?.id ? generatePersonalMeetingId(user.id) : '000 000 0000';
  const displayedPersonalMeetingId = showPersonalMeetingId
    ? personalMeetingId
    : maskPersonalMeetingId(personalMeetingId);

  const fetchMeetings = useCallback(async () => {
    try {
      setLoading(true);
      const data = await meetingService.getMeetings();
      setMeetings(filterUpcomingMeetings(data));
    } catch (error) {
      console.error('Toplantılar yüklenemedi:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchMeetings();
    const interval = window.setInterval(() => {
      if (document.visibilityState === 'hidden') return;
      void fetchMeetings();
    }, 30_000);
    return () => window.clearInterval(interval);
  }, [fetchMeetings]);

  const filteredMeetings = useMemo(() => {
    return meetings.filter((m) => {
      if (activeTab === 'online') return m.meetingFormat !== 'IN_PERSON';
      if (activeTab === 'inperson') return m.meetingFormat === 'IN_PERSON';
      return true;
    });
  }, [meetings, activeTab]);

  const onlineMeetingsCount = useMemo(
    () => meetings.filter((m) => m.meetingFormat !== 'IN_PERSON').length,
    [meetings]
  );
  const inPersonMeetingsCount = useMemo(
    () => meetings.filter((m) => m.meetingFormat === 'IN_PERSON').length,
    [meetings]
  );

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    let dateLabel = '';
    if (date.toDateString() === today.toDateString()) {
      dateLabel = 'Bugün';
    } else if (date.toDateString() === tomorrow.toDateString()) {
      dateLabel = 'Yarın';
    } else {
      dateLabel = date.toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' });
    }

    const time = date.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
    return `${dateLabel}, ${time}`;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('tr-TR', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('tr-TR', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const formatShortDate = (dateString: string) => {
    const date = new Date(dateString);
    const day = date.getDate();
    const month = date.toLocaleDateString('tr-TR', { month: 'short' }).toUpperCase();
    const year = date.getFullYear();
    return { day, month, year };
  };

  const handleSchedule = () => navigate('/plans?create=true');
  const handleJoin = () => {
    setJoinError('');
    setShowJoinModal(true);
  };
  const confirmJoin = async () => {
    const code = meetingIdInput.trim();
    if (!code) return;

    const result = await validateMeetingLinkAccess(code);
    if (!result.allowed && result.expired) {
      setJoinError('Bu toplantı linkinin süresi dolmuş (1 saat).');
      return;
    }

    setShowJoinModal(false);
    navigate(`/meeting/${code}`);
    setMeetingIdInput('');
    setJoinError('');
  };
  const cancelJoin = () => {
    setShowJoinModal(false);
    setMeetingIdInput('');
    setJoinError('');
  };
  const handleHost = async () => {
    const meetingId = await createInstantOnlineMeeting();
    const limitSeconds = getMeetingDurationLimitSeconds(getUserPlan(user));
    if (limitSeconds != null) {
      writeRoomTimePolicy(meetingId, createRoomTimePolicy(limitSeconds));
    }
    navigate(`/meeting/${meetingId}`);
  };
  const handleCopyId = () => {
    navigator.clipboard.writeText(personalMeetingId.replace(/\s/g, ''));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  const handleJoinMeeting = async (meeting: Meeting) => {
    if (
      meeting.meetingFormat !== 'IN_PERSON' &&
      !meeting.isInstant &&
      meeting.status === 'SCHEDULED'
    ) {
      try {
        await meetingService.startMeeting(meeting.id);
      } catch (error) {
        console.error('Toplantı başlatılamadı:', error);
        return;
      }
    }

    const limitSeconds = resolveRoomDurationLimitSeconds(
      getUserPlan(user),
      meeting.duration
    );
    if (limitSeconds != null) {
      writeRoomTimePolicy(
        meeting.meetingCode,
        createRoomTimePolicy(limitSeconds, Date.now())
      );
    }
    navigate(`/meeting/${meeting.meetingCode}`);
  };  const isInPersonMeeting = (meeting: Meeting): boolean => meeting.meetingFormat === 'IN_PERSON';

  const openInvitationModal = (meeting: Meeting) => {
    setSelectedMeeting(meeting);
    setShowInvitationModal(true);
  };

  const generateInvitationImage = async (meeting: Meeting): Promise<string> => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d')!;
    
    canvas.width = 1200;
    canvas.height = 675;

    const gradient = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
    gradient.addColorStop(0, '#1e1b4b');
    gradient.addColorStop(0.5, '#312e81');
    gradient.addColorStop(1, '#581c87');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    ctx.beginPath();
    ctx.arc(-50, 100, 200, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(168, 85, 247, 0.15)';
    ctx.fill();

    ctx.beginPath();
    ctx.arc(1250, 600, 250, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(236, 72, 153, 0.15)';
    ctx.fill();

    ctx.fillStyle = 'rgba(255, 255, 255, 0.1)';
    ctx.beginPath();
    ctx.roundRect(50, 50, 200, 575, 20);
    ctx.fill();

    const dateInfo = formatShortDate(meeting.scheduledAt || '');
    
    ctx.fillStyle = '#a855f7';
    ctx.font = 'bold 28px system-ui';
    ctx.textAlign = 'center';
    ctx.fillText(dateInfo.month, 150, 120);

    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 80px system-ui';
    ctx.fillText(dateInfo.day.toString(), 150, 210);

    ctx.fillStyle = '#a1a1aa';
    ctx.font = '24px system-ui';
    ctx.fillText(dateInfo.year.toString(), 150, 250);

    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 36px system-ui';
    const time = formatTime(meeting.scheduledAt || '');
    ctx.fillText(time, 150, 340);

    ctx.fillStyle = '#a1a1aa';
    ctx.font = '20px system-ui';
    ctx.fillText(`${meeting.duration} dakika`, 150, 380);

    ctx.fillStyle = '#22c55e';
    ctx.beginPath();
    ctx.arc(150, 480, 35, 0, Math.PI * 2);
    ctx.fill();
    
    ctx.fillStyle = '#ffffff';
    ctx.font = '30px system-ui';
    ctx.fillText('📍', 150, 490);

    ctx.fillStyle = '#22c55e';
    ctx.font = 'bold 16px system-ui';
    ctx.fillText('YÜZ YÜZE', 150, 550);

    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 42px system-ui';
    ctx.textAlign = 'left';
    
    let title = meeting.title;
    if (ctx.measureText(title).width > 650) {
      title = title.substring(0, 30) + '...';
    }
    ctx.fillText(title, 300, 120);

    if (meeting.description) {
      ctx.fillStyle = '#a1a1aa';
      ctx.font = '22px system-ui';
      let desc = meeting.description;
      if (desc.length > 60) desc = desc.substring(0, 60) + '...';
      ctx.fillText(desc, 300, 165);
    }

    ctx.beginPath();
    ctx.moveTo(300, 200);
    ctx.lineTo(1100, 200);
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.1)';
    ctx.lineWidth = 2;
    ctx.stroke();

    let yPos = 260;

    if (meeting.location) {
      ctx.fillStyle = '#22c55e';
      ctx.font = '18px system-ui';
      ctx.fillText('MEKAN', 300, yPos);
      
      ctx.fillStyle = '#ffffff';
      ctx.font = 'bold 28px system-ui';
      ctx.fillText(meeting.location, 300, yPos + 35);
      yPos += 90;
    }

    if (meeting.building || meeting.room) {
      ctx.fillStyle = '#a855f7';
      ctx.font = '18px system-ui';
      ctx.fillText('BİNA / ODA', 300, yPos);
      
      ctx.fillStyle = '#ffffff';
      ctx.font = 'bold 24px system-ui';
      const buildingRoom = [meeting.building, meeting.room].filter(Boolean).join(' • ');
      ctx.fillText(buildingRoom, 300, yPos + 35);
      yPos += 90;
    }

    if (meeting.address) {
      ctx.fillStyle = '#6b7280';
      ctx.font = '18px system-ui';
      ctx.fillText('ADRES', 300, yPos);
      
      ctx.fillStyle = '#d1d5db';
      ctx.font = '20px system-ui';
      let addr = meeting.address;
      if (addr.length > 50) addr = addr.substring(0, 50) + '...';
      ctx.fillText(addr, 300, yPos + 30);
      yPos += 70;
    }

    ctx.beginPath();
    ctx.moveTo(300, 550);
    ctx.lineTo(1100, 550);
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.1)';
    ctx.lineWidth = 1;
    ctx.stroke();

    if (meeting.participants && meeting.participants.length > 0) {
      ctx.fillStyle = '#a1a1aa';
      ctx.font = '18px system-ui';
      ctx.fillText(`👥 ${meeting.participants.length} katılımcı davet edildi`, 300, 600);
    }

    ctx.fillStyle = '#6b7280';
    ctx.font = '16px system-ui';
    ctx.textAlign = 'right';
    ctx.fillText('TBKMeet ile oluşturuldu', 1100, 600);

    ctx.fillStyle = 'rgba(168, 85, 247, 0.3)';
    ctx.beginPath();
    ctx.roundRect(900, 40, 200, 40, 20);
    ctx.fill();

    ctx.fillStyle = '#c4b5fd';
    ctx.font = 'bold 14px system-ui';
    ctx.textAlign = 'center';
    ctx.fillText('TOPLANTI DAVETİYESİ', 1000, 67);

    return canvas.toDataURL('image/png');
  };

  const handleDownloadInvitation = async () => {
    if (!selectedMeeting) return;
    setDownloading(true);
    try {
      const imageData = await generateInvitationImage(selectedMeeting);
      const link = document.createElement('a');
      link.download = `davetiye-${selectedMeeting.meetingCode}.png`;
      link.href = imageData;
      link.click();
    } catch (error) {
      console.error('Davetiye oluşturulamadı:', error);
    } finally {
      setDownloading(false);
    }
  };

  const handleShareInvitation = async () => {
    if (!selectedMeeting) return;
    const shareText = `📌 ${selectedMeeting.title}

📅 ${formatDate(selectedMeeting.scheduledAt || '')}
🕐 ${formatTime(selectedMeeting.scheduledAt || '')} (${selectedMeeting.duration} dk)

📍 ${selectedMeeting.location || ''}
${selectedMeeting.building ? `🏢 ${selectedMeeting.building}` : ''}${selectedMeeting.room ? ` • ${selectedMeeting.room}` : ''}
${selectedMeeting.address ? `📫 ${selectedMeeting.address}` : ''}

Katılımınızı bekliyoruz!`;

    if (navigator.share) {
      try {
        await navigator.share({ title: `Davetiye: ${selectedMeeting.title}`, text: shareText });
      } catch (err) {}
    } else {
      await navigator.clipboard.writeText(shareText);
      alert('Davetiye metni panoya kopyalandı!');
    }
  };

  return (
    <>
      <div className="space-y-4">
        {/* Hızlı Aksiyonlar */}
        <div className="bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 p-6">
          <h3 className="text-lg font-semibold text-white mb-4">Hızlı Aksiyonlar</h3>
          <div className="grid grid-cols-3 gap-3 mb-4">
            <button
              onClick={handleSchedule}
              className="flex flex-col items-center justify-center p-4 bg-purple-600 hover:bg-purple-700 rounded-lg transition transform hover:scale-105"
            >
              <Calendar className="w-6 h-6 text-white mb-2" />
              <span className="text-white text-xs font-semibold">Planla</span>
            </button>
            <button
              onClick={handleJoin}
              className="flex flex-col items-center justify-center p-4 bg-purple-600 hover:bg-purple-700 rounded-lg transition transform hover:scale-105"
            >
              <UserPlus className="w-6 h-6 text-white mb-2" />
              <span className="text-white text-xs font-semibold">Katıl</span>
            </button>
            <button
              onClick={handleHost}
              className="flex flex-col items-center justify-center p-4 bg-purple-600 hover:bg-purple-700 rounded-lg transition transform hover:scale-105"
            >
              <Video className="w-6 h-6 text-white mb-2" />
              <span className="text-white text-xs font-semibold">Hızlı Toplantı</span>
            </button>
          </div>
          <p className="text-gray-500 text-xs mb-4">
            Hızlı toplantı anında başlar; planladığınız toplantılardan ayrıdır.
          </p>
          <div className="pt-4 border-t border-white/10">
            <div className="flex items-center justify-between">
              <span className="text-gray-400 text-sm">Kişisel Toplantı Kimliği</span>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => setShowPersonalMeetingId((prev) => !prev)}
                  className="p-1.5 rounded-lg text-gray-400 hover:text-white hover:bg-white/10 transition"
                  aria-label={showPersonalMeetingId ? 'Kimliği gizle' : 'Kimliği göster'}
                  title={showPersonalMeetingId ? 'Gizle' : 'Göster'}
                >
                  {showPersonalMeetingId ? (
                    <EyeOff className="w-4 h-4" />
                  ) : (
                    <Eye className="w-4 h-4" />
                  )}
                </button>
                <button
                  type="button"
                  onClick={handleCopyId}
                  className="p-1.5 rounded-lg text-purple-400 hover:text-purple-300 hover:bg-white/10 transition"
                  aria-label="Kimliği kopyala"
                  title="Kopyala"
                >
                  {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <p className="text-white font-mono text-lg mt-2 tracking-wide">
              {displayedPersonalMeetingId}
            </p>
          </div>
        </div>

        {/* Toplantılar */}
        <div className="bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white">Toplantılar</h3>
            <Link to="/plans" className="text-purple-400 hover:text-purple-300 text-sm transition">
              Tümünü Gör
            </Link>
          </div>

          {/* Kategori Tabları */}
          <div className="flex gap-2 mb-4">
            <button
              onClick={() => setActiveTab('all')}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition ${
                activeTab === 'all' 
                  ? 'bg-purple-600 text-white' 
                  : 'bg-white/5 text-gray-400 hover:bg-white/10'
              }`}
            >
              Tümü ({meetings.length})
            </button>
            <button
              onClick={() => setActiveTab('online')}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition flex items-center gap-1 ${
                activeTab === 'online' 
                  ? 'bg-purple-600 text-white' 
                  : 'bg-white/5 text-gray-400 hover:bg-white/10'
              }`}
            >
              <Video className="w-3 h-3" />
              Online ({onlineMeetingsCount})
            </button>
            <button
              onClick={() => setActiveTab('inperson')}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition flex items-center gap-1 ${
                activeTab === 'inperson' 
                  ? 'bg-green-600 text-white' 
                  : 'bg-white/5 text-gray-400 hover:bg-white/10'
              }`}
            >
              <MapPin className="w-3 h-3" />
              Fiziksel ({inPersonMeetingsCount})
            </button>
          </div>
          
          {/* Sabit yükseklikli içerik alanı */}
          <div className="h-72">
            {loading ? (
              <div className="flex items-center justify-center h-full">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-500"></div>
              </div>
            ) : filteredMeetings.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full">
                <Video className="w-12 h-12 text-gray-600 mb-3" />
                <p className="text-gray-400 mb-4">
                  {activeTab === 'all' ? 'Yaklaşan Toplantı Yok' : 
                   activeTab === 'online' ? 'Online Toplantı Yok' : 'Fiziksel Toplantı Yok'}
                </p>
                <button
                  onClick={handleSchedule}
                  className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-semibold transition"
                >
                  Toplantı Planla
                </button>
              </div>
            ) : (
              <div className="h-full overflow-y-auto space-y-2 pr-2 custom-scrollbar">
                {filteredMeetings.map((meeting) => (
                  <div 
                    key={meeting.id}
                    className={`bg-white/5 rounded-lg p-3 border transition ${
                      isInPersonMeeting(meeting) 
                        ? 'border-green-500/20 hover:border-green-500/40' 
                        : 'border-white/5 hover:border-purple-500/30'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h4 className="text-white font-medium text-sm truncate">{meeting.title}</h4>
                          {isInPersonMeeting(meeting) ? (
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-green-600/20 text-green-400 rounded text-[10px] shrink-0">
                              <MapPin className="w-2.5 h-2.5" />
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-purple-600/20 text-purple-400 rounded text-[10px] shrink-0">
                              <Video className="w-2.5 h-2.5" />
                            </span>
                          )}
                        </div>
                        {meeting.scheduledAt && (
                          <div className="flex items-center gap-1 mt-1 text-gray-400 text-xs">
                            <Clock className="w-3 h-3" />
                            <span>{formatDateTime(meeting.scheduledAt)}</span>
                          </div>
                        )}
                        {isInPersonMeeting(meeting) && meeting.location && (
                          <div className="flex items-center gap-1 mt-1 text-green-400/70 text-xs">
                            <MapPin className="w-3 h-3" />
                            <span className="truncate">{meeting.location}</span>
                          </div>
                        )}
                        <p className="text-gray-500 text-xs mt-1 font-mono">{meeting.meetingCode}</p>
                      </div>
                      {isInPersonMeeting(meeting) ? (
                        <button
                          onClick={() => openInvitationModal(meeting)}
                          className="shrink-0 p-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition"
                          title="Davetiye Oluştur"
                        >
                          <Mail className="w-4 h-4" />
                        </button>
                      ) : (
                        <button
                          onClick={() => handleJoinMeeting(meeting)}
                          className="shrink-0 p-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition"
                          title="Toplantıya katıl"
                          aria-label="Toplantıya katıl"
                        >
                          <Play className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Toplantıya Katıl Modal */}
      {showJoinModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-2xl border border-white/10 p-6 w-full max-w-md shadow-2xl">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-white">Toplantıya Katıl</h3>
              <button
                onClick={cancelJoin}
                className="p-2 rounded-lg hover:bg-white/10 text-gray-400 hover:text-white transition"
                title="Kapat"
                aria-label="Kapat"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Toplantı ID veya Link
              </label>
              <input
                type="text"
                value={meetingIdInput}
                onChange={(e) => {
                  setMeetingIdInput(e.target.value);
                  setJoinError('');
                }}
                onKeyDown={(e) => e.key === 'Enter' && void confirmJoin()}
                placeholder="Toplantı ID'sini girin"
                className="w-full px-4 py-3 bg-slate-700 border border-white/10 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500"
                autoFocus
              />
              {joinError && (
                <p className="mt-2 text-sm text-red-400">{joinError}</p>
              )}
            </div>

            <div className="flex space-x-3">
              <button
                onClick={cancelJoin}
                className="flex-1 px-4 py-2.5 bg-white/10 hover:bg-white/20 text-white rounded-xl font-medium transition"
              >
                İptal
              </button>
              <button
                onClick={confirmJoin}
                disabled={!meetingIdInput.trim()}
                className="flex-1 px-4 py-2.5 bg-purple-600 hover:bg-purple-700 disabled:bg-purple-600/50 disabled:cursor-not-allowed text-white rounded-xl font-medium transition"
              >
                Katıl
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Davetiye Modal */}
      {showInvitationModal && selectedMeeting && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-slate-900 rounded-2xl border border-white/10 w-full max-w-3xl shadow-2xl">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-white/10">
              <h3 className="text-lg font-bold text-white">Toplantı Davetiyesi</h3>
              <button
                onClick={() => setShowInvitationModal(false)}
                className="p-2 rounded-lg hover:bg-white/10 text-gray-400 hover:text-white transition"
                title="Kapat"
                aria-label="Kapat"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Yatay Davetiye Kartı */}
            <div className="p-4">
              <div className="bg-gradient-to-r from-indigo-900 via-purple-900 to-pink-900 rounded-xl overflow-hidden">
                <div className="flex">
                  {/* Sol - Tarih Bloğu */}
                  <div className="w-32 sm:w-40 bg-white/5 flex flex-col items-center justify-center p-4 border-r border-white/10">
                    <span className="text-purple-400 font-bold text-sm">{formatShortDate(selectedMeeting.scheduledAt || '').month}</span>
                    <span className="text-white font-bold text-5xl sm:text-6xl">{formatShortDate(selectedMeeting.scheduledAt || '').day}</span>
                    <span className="text-gray-400 text-sm">{formatShortDate(selectedMeeting.scheduledAt || '').year}</span>
                    <div className="mt-4 pt-4 border-t border-white/10 text-center">
                      <span className="text-white font-bold text-xl">{formatTime(selectedMeeting.scheduledAt || '')}</span>
                      <p className="text-gray-400 text-xs mt-1">{selectedMeeting.duration} dakika</p>
                    </div>
                    <div className="mt-4 pt-4 border-t border-white/10">
                      <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center">
                        <MapPin className="w-5 h-5 text-white" />
                      </div>
                      <p className="text-green-400 text-xs font-bold mt-2">YÜZ YÜZE</p>
                    </div>
                  </div>

                  {/* Sağ - İçerik */}
                  <div className="flex-1 p-5">
                    <div className="flex justify-end mb-2">
                      <span className="px-3 py-1 bg-purple-500/30 text-purple-300 text-xs font-bold rounded-full">
                        TOPLANTI DAVETİYESİ
                      </span>
                    </div>

                    <h4 className="text-2xl sm:text-3xl font-bold text-white mb-1">{selectedMeeting.title}</h4>
                    {selectedMeeting.description && (
                      <p className="text-gray-400 text-sm mb-4 line-clamp-1">{selectedMeeting.description}</p>
                    )}

                    <div className="space-y-3 mt-4 pt-4 border-t border-white/10">
                      {selectedMeeting.location && (
                        <div>
                          <p className="text-green-400 text-xs font-bold uppercase">Mekan</p>
                          <p className="text-white font-semibold">{selectedMeeting.location}</p>
                        </div>
                      )}
                      
                      {(selectedMeeting.building || selectedMeeting.room) && (
                        <div>
                          <p className="text-purple-400 text-xs font-bold uppercase">Bina / Oda</p>
                          <p className="text-white">{[selectedMeeting.building, selectedMeeting.room].filter(Boolean).join(' • ')}</p>
                        </div>
                      )}

                      {selectedMeeting.address && (
                        <div>
                          <p className="text-gray-500 text-xs font-bold uppercase">Adres</p>
                          <p className="text-gray-300 text-sm">{selectedMeeting.address}</p>
                        </div>
                      )}
                    </div>

                    <div className="flex items-center justify-between mt-4 pt-4 border-t border-white/10">
                      {selectedMeeting.participants && selectedMeeting.participants.length > 0 && (
                        <span className="text-gray-400 text-sm">
                          👥 {selectedMeeting.participants.length} katılımcı
                        </span>
                      )}
                      <span className="text-gray-500 text-xs">TBKMeet ile oluşturuldu</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Butonlar */}
            <div className="flex gap-3 p-4 border-t border-white/10">
              <button
                onClick={handleDownloadInvitation}
                disabled={downloading}
                className="flex-1 px-4 py-2.5 bg-white/10 hover:bg-white/20 text-white rounded-xl font-medium transition flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {downloading ? (
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <Download className="w-4 h-4" />
                )}
                <span>PNG İndir</span>
              </button>
              <button
                onClick={handleShareInvitation}
                className="flex-1 px-4 py-2.5 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl font-medium transition flex items-center justify-center gap-2"
              >
                <Share2 className="w-4 h-4" />
                <span>Paylaş</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Custom Scrollbar Styles */}
      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(139, 92, 246, 0.3);
          border-radius: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(139, 92, 246, 0.5);
        }
      `}</style>
    </>
  );
};

export default QuickActions;